import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Flex, Drawer, Dropdown } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import {
  Header,
  HeaderAvatar,
  Button,
  ButtonType,
  blueGradientHeaderTheme,
  AIStarsIcon,
} from '@bettercomp/ui-components';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { useFeatureFlag } from 'hooks/featureFlags/useFeatureFlag';
import { PATH } from 'common/constants';
import { NavMenu } from 'components/NavMenu';
import { logout } from 'service/cognitoService';
import { selectUser } from 'redux/user/selectors';
import { FeatureFlag, PermissionCode } from 'types';

import { Notifications } from './Notifications';
import { TenancySwitcher } from '../TenancySwitcher';
import MobileMenuIcon from './menu.svg';
import cn from './styles.module.scss';
import Image from 'next/image';

const userMenuItems = [
  {
    key: 'signOut',
    label: (
      <div onClick={() => logout()} data-testid="signOutButton">
        SIGN OUT
      </div>
    ),
  },
];

export function AppHeader() {
  const user = useSelector(selectUser);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const { permitted: canManageFiles } = useHasPermissions({ permissions: [PermissionCode.LOAD_FILES] });
  const { permitted: canManageDataLoaders } = useHasPermissions({ permissions: [PermissionCode.MANAGE_DATA_LOADER] });
  const { permitted: canAskBettercomp } = useHasPermissions({ permissions: [PermissionCode.ASK_BETTERCOMP] });
  const isAiChatEnabled = useFeatureFlag(FeatureFlag.AI_CHAT);

  const router = useRouter();

  const backHome = () => {
    router.push('/');
  };

  const handleCloudClick = () => {
    if (canManageFiles) {
      router.push(PATH.FILES_UPLOAD);
    }
    if (canManageDataLoaders) {
      router.push(PATH.DATA_LOADERS);
    }
  };

  return (
    <Header theme={blueGradientHeaderTheme}>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={12}>
          <Image
            width={130}
            height={24}
            src="/assets/images/logo/bc-logo-knockout.png"
            alt="Bettercomp Logo"
            onClick={backHome}
            style={{ cursor: 'pointer' }}
          />
          <TenancySwitcher />
        </Flex>

        {user && (
          <>
            <Flex flex={1} className={cn.desktopMenu} style={{ minWidth: 14 }}>
              <NavMenu />
            </Flex>

            <Flex align="center" gap={8}>
              {isAiChatEnabled && canAskBettercomp && (
                <Button
                  size="medium"
                  type={ButtonType.Icon}
                  icon={<AIStarsIcon style={{ fontSize: 18 }} />}
                  onClick={() => router.push(PATH.AI_ASSISTANT)}
                  data-testid="ai-assistant-nav-button"
                  style={{ background: "transparent" }}
                />
              )}
              {(canManageFiles || canManageDataLoaders) && (
                <Button
                  size="medium"
                  type={ButtonType.Icon}
                  icon={<CloudUploadOutlined style={{ fontSize: 18 }} />}
                  onClick={handleCloudClick}
                  style={{ background: "transparent" }}
                />
              )}
              <Notifications />
              <Dropdown
                placement="bottomRight"
                forceRender
                menu={{ items: userMenuItems }}
              >
                <div style={{ cursor: 'pointer' }}>
                  <HeaderAvatar>
                    {user.firstName?.charAt(0).toUpperCase()}
                  </HeaderAvatar>
                </div>
              </Dropdown>
              <MobileMenuIcon className={cn.mobileMenuIcon} onClick={() => setOpenMobileMenu(true)} />
              <Drawer
                rootClassName={cn.drawer}
                placement="right"
                closable={false}
                onClose={() => setOpenMobileMenu(false)}
                open={openMobileMenu}
              >
                <NavMenu
                  mode="vertical"
                  onClick={() => setOpenMobileMenu(false)}
                />
              </Drawer>
            </Flex>
          </>
        )}
      </Flex>
    </Header>
  );
}
